import { Component, OnInit, Output, EventEmitter, Input } from "@angular/core";
import { DRONE_RESOURCE_TYPES } from 'src/app/shared/constants/drone-resource-options';
import { DroneResource } from 'src/app/core/data-models/drone-resource.model';
import { DroneResourceService } from "src/app/core/data-services/drone-resource.service";
import { DroneResourceUpload } from "src/app/core/request-models/drone-resource-upload.model";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { RemoveDashPipe } from "../pipes/remove-dash.pipe";
import { CommonModule } from '@angular/common';
import { FileUploadService } from "src/app/core/api-services/file-upload.service";
import { openExternal } from '../../core/utils/external-link-utils';

@Component({
    selector: 'app-drone-resource-upload-modal',
    standalone: true,
    templateUrl: './drone-resource-upload-modal.component.html',
    styleUrls: ['./drone-resource-upload-modal.component.scss'],
    imports: [CommonModule, ReactiveFormsModule, RemoveDashPipe]
})
export class DroneResourceUploadModalComponent implements OnInit {
    @Input() droneId?: string;
    @Input() resource?: DroneResource | null;
    @Output() close = new EventEmitter<void>();
    @Output() uploaded = new EventEmitter<void>();
    @Output() newItem = new EventEmitter<DroneResource>();
    @Output() updatedItem = new EventEmitter<DroneResource>();

    resourceForm: FormGroup;

    // File upload — deliberately NOT a form control. The file goes to S3 via
    // presign + PUT, and only the resulting s3Key/cdnUrl ride along in the
    // register-row payload, so it isn't part of this form's value.
    selectedFile: File | null = null;
    uploadedFileUrl = '';
    uploadedFileName = '';

    existingFileUrl = '';
    existingFileName = '';

    // UI state
    uploading = false;
    errorMessage = '';

    loadingExisting = false;

    resourceTypes = DRONE_RESOURCE_TYPES;

    constructor(
        private fb: FormBuilder,
        private droneResourceService: DroneResourceService,
        private fileUploadService: FileUploadService
    ) {
        this.resourceForm = this.fb.group({
            resourceType: ['', Validators.required],
            otherType: [''],
            caption: [''],
            // Defaults closed, matching adn_drone_resources.admin_only.
            adminOnly: [true]
        });
    }

    ngOnInit(): void {
        // Starts disabled — nothing to name until "Other" is picked.
        this.resourceForm.get('otherType')!.disable({ emitEvent: false });

        this.resourceForm.get('resourceType')!.valueChanges.subscribe(value => {
            const otherType = this.resourceForm.get('otherType')!;

            if (value === 'other') {
                otherType.setValidators([Validators.required]);
                otherType.enable({ emitEvent: false });
            } else {
                otherType.clearValidators();
                otherType.setValue('', { emitEvent: false });
                otherType.disable({ emitEvent: false });
            }

            otherType.updateValueAndValidity({ emitEvent: false });
        });

        if (this.editMode) {
            this.loadingExisting = true;
            this.setupEdit();
        }
    }

    async setupEdit(): Promise<void> {
        if (this.resourceTypes.includes(this.resource?.resourceType ?? '')){
            this.resourceForm.get('resourceType')?.setValue(this.resource?.resourceType);
        } else {
            this.resourceForm.get('resourceType')?.setValue('other');
            this.resourceForm.get('otherType')?.setValue(this.resource?.resourceType);
        }

        this.resourceForm.get('adminOnly')?.setValue(this.resource?.adminOnly ?? true);
        this.resourceForm.get('caption')?.setValue(this.resource?.caption ?? '');
        this.existingFileName = this.resource?.fileName ?? '';
        if (this.droneId && this.resource?.id) {
            try{
                this.existingFileUrl = await this.droneResourceService.getResourceDownloadUrl(this.droneId, this.resource.id);
            } catch (error) {
                this.errorMessage = 'Could not get file url'
            }
        } else {
            this.existingFileUrl = '';
        }
        this.loadingExisting = false;
    }

    get editMode(): boolean {
        return !!this.resource;
    }

    /** True once every required field is filled and a file has been chosen. */
    get canSubmit(): boolean {
        return this.resourceForm.valid
            && (this.editMode || !!this.selectedFile)
            && !this.uploading;
    }

    get effectiveResourceType(): string {
        const { resourceType, otherType } = this.resourceForm.getRawValue();
        return resourceType === 'other' ? (otherType ?? '').trim() : resourceType;
    }

    formatFileSize(bytes: number): string {
        return this.fileUploadService.formatFileSize(bytes);
    }

    /** Inline-error helper: only show once the user has interacted. */
    showError(controlName: string): boolean {
        const control = this.resourceForm.get(controlName);
        return !!control && control.invalid && (control.dirty || control.touched);
    }

    async onSubmit(): Promise<void> {
        this.resourceForm.markAllAsTouched();

        if (!this.editMode && !this.selectedFile) {
            this.errorMessage = 'Choose a file to upload.';
            return;
        }

        if (!this.canSubmit) {
            this.errorMessage = 'Fill in the required fields before submitting.';
            return;
        }

        if (await this.uploadFile()) {
            this.onClose();
        }
    }

    onClose(): void {
        this.close.emit();
    }

    /** Backdrop dismissal is blocked mid-upload so feedback isn't lost. */
    onBackdropClick(): void {
        if (!this.uploading) {
            this.onClose();
        }
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
          const file = input.files[0];
          const validationError = this.fileUploadService.validate(file);
          if (validationError) {
              this.errorMessage = validationError;
              this.selectedFile = null;
              input.value = '';
              return;
          }
          this.selectedFile = file;
          this.uploadedFileName = file.name;
          this.errorMessage = '';
        }
    }

    get maxFileSizeLabel(): string {
        return this.fileUploadService.formatFileSize(this.fileUploadService.getMaxFileSize());
    }

    openExistingFile(): void {
        if (!this.existingFileUrl) return;
        openExternal(this.existingFileUrl);
    }

    async uploadFile(): Promise<boolean> {
        if (!this.droneId) {
            this.errorMessage = 'No drone selected.';
            return false;
        }

        // Only a new resource needs a file — an edit can change metadata alone.
        if (!this.editMode && this.selectedFile === null) {
            this.errorMessage = 'Choose a file to upload.';
            return false;
        }

        this.uploading = true;
        this.errorMessage = '';

        const meta: DroneResourceUpload = {
            resourceType: this.effectiveResourceType,
            adminOnly: this.resourceForm.get('adminOnly')!.value,
            caption: this.resourceForm.get('caption')!.value || undefined,
        };

        if (this.editMode && this.resource) {
            try {
                const response = await this.droneResourceService.updateResource(
                    this.droneId,
                    this.resource.id,
                    meta,
                    this.selectedFile
                );
                this.updatedItem.emit(response);
                this.uploaded.emit();
                return true;
            } catch (error: any) {
                console.error('Could not update resource', error);
                this.errorMessage = error?.message || 'Could not save your changes.';
                return false;
            } finally {
                this.uploading = false;
            }
        }

        try {
            const response = await this.droneResourceService.uploadResourceByDroneId(
                this.droneId,
                this.selectedFile!,
                meta
            );
            this.newItem.emit(response);
            this.uploaded.emit();
            return true;
        } catch (error: any) {
            console.error('Could not upload resource', error);
            this.errorMessage = error?.message || 'Could not upload resource.';
            return false;
        } finally {
            this.uploading = false;
        }
    }
}
